// ─── LA RIGA DI CHI PAGA: le regole pure, senza React attorno ────────────────
//
// ⭐ Stanno in un file loro perché sono l'unica parte dell'incasso che si può
// mettere al banco: il resto è stato di una schermata, e un banco che volesse
// provarlo dovrebbe montare un browser finto. Qui invece ci sono tre domande
// che hanno una risposta sola — «quanto ha già dato?», «quanto resta?», «che
// cifra mostra la casella?» — e sono esattamente quelle che hanno sbagliato.
// 📌 *Ciò che si può provare senza finzioni si separa da ciò che non si può:
//    altrimenti non si prova nessuno dei due.*

import type { CassaDellaPrenotazione, MetodoIncasso } from '@pmo/shared';

/** Chi è in campo, per quel poco che alla cassa serve saperne. */
export interface ChiPaga {
  id: string;
  memberId: string | null;
  displayName: string;
  amountDueCents: number;
}

/** Com'è messa la riga di uno mentre si compila l'incasso. */
export interface Scelta {
  scelto: boolean;
  /** Il TESTO della casella, non i centesimi: qui dentro si sta ancora battendo. */
  importo: string;
  metodo: MetodoIncasso;
}

/**
 * Quanto ha già pagato **questo posto in campo** su questa partita.
 *
 * 💶 Somma NUDA, storni compresi: una coppia incasso+storno vale zero da sé, e
 * non c'è nessuna riga da ricordarsi di escludere. È la stessa forma che usa il
 * riepilogo, e le due non possono divergere.
 *
 * 🚨🔄⭐⭐ **E FINO AL 18/09/2026 CHIEDEVA UN'ALTRA COSA: «quanto ha pagato chi
 * SI CHIAMA così».** Per un socio è lo stesso — l'id c'è. Per un **ospite** no,
 * e due ospiti chiamati allo stesso modo nella stessa prenotazione sono
 * ammessi di proposito.
 * 📏 Misurato sull'app viva: due ospiti «Ospite» da 10,00 € a carico, incassati
 *   10,00 € **da uno solo** ⇒ la scheda scriveva *«già 10,00 €»* e residuo
 *   **0,00** su **tutti e due**. In cassa una riga sola: 10,00 € mancanti, e
 *   nessun errore da nessuna parte.
 * ⇒ Adesso si guarda `playerId`, che è **il posto**, non la persona: due
 *   omonimi occupano due posti, quindi hanno due conti.
 *
 * ⚠️ **Il ripiego sul nome resta, e serve alle righe vecchie**: quelle scritte
 * prima della migrazione non hanno nessun posto addosso, e senza il ripiego un
 * incasso di ieri sparirebbe dal conto di oggi — cioè la scheda chiederebbe
 * una seconda volta denaro già preso. 📌 *Una cura che cancella il passato non
 * è una cura: è lo stesso difetto girato dall'altra parte.*
 *
 * 🚨🔄⭐⭐ **E LA CURA DI STAMATTINA NE AVEVA APERTO UNO SUO, misurato il
 * 18/09/2026 pomeriggio sull'app viva: UN POSTO CHE NON C'È PIÙ.**
 * 📏 Il gesto, e non è un caso di scuola — è quello che si fa per correggere
 *   uno sbaglio: si incassano 11,00 € da un socio, lo si **toglie** dalla
 *   partita, lo si **rimette**. Uscendo, la sua riga di `booking_players`
 *   viene **cancellata davvero**; rientrando ne prende una nuova, con un id
 *   nuovo. ⇒ L'incasso porta addosso il posto **di prima**, che non esiste
 *   più: nessuna riga della tabella se lo prendeva, e la scheda gli chiedeva
 *   di nuovo **tutto**. In testa diceva «11,00 € incassati» e nella sua riga
 *   «13,00 €» — due numeri della stessa cosa, come stamattina.
 * ⚖️ Il ripiego sul nome non bastava a prenderlo: quell'incasso **un posto ce
 *   l'ha**, ed è solo morto. ⇒ La domanda giusta non è *«questa riga porta un
 *   posto?»* ma *«il posto che porta è ancora in campo?»*.
 * 📌 *Un riferimento non si giudica da com'è fatto ma da dove punta: uno che
 *    punta a una riga cancellata è pieno e non vale niente, e distinguerlo da
 *    uno vuoto si può solo guardando dall'altra parte.*
 */
export function giaPagatoCents(
  cassa: CassaDellaPrenotazione,
  chi: ChiPaga,
  inCampo: readonly ChiPaga[],
): number {
  const postiVivi = new Set(inCampo.map((g) => g.id));
  return cassa.payments
    .filter((p) => {
      /*
       * ① **La riga vecchia**, nata prima che i posti esistessero: di posto non
       * ne conosce nessuno, e l'unica cosa che ha è la persona. È il ripiego
       * descritto qui sopra, e resta identico a com'era.
       */
      if (p.playerId === null) {
        return chi.memberId ? p.memberId === chi.memberId : p.playerName === chi.displayName;
      }
      /* ② **Il posto c'è ancora**: è lui che ha preso quel denaro, e basta. */
      if (postiVivi.has(p.playerId)) return p.playerId === chi.id;
      /*
       * ③ **Il posto NON c'è più.** Chi ha pagato è uscito dalla partita — o,
       * molto più spesso, è stato tolto e rimesso per correggere qualcosa, e
       * rientrando ha preso un posto nuovo.
       * ⇒ Si torna a guardare **la persona**, ma solo dove la persona si
       *   riconosce con certezza: un **socio** ha un id suo, e quel legame non
       *   dipende da come è scritto un nome.
       * ⛔ Per un OSPITE ci si ferma qui, e non è una dimenticanza: il suo
       *   unico segno era il posto, e due ospiti si possono chiamare uguale
       *   (è il difetto curato stamattina). Attribuire quel denaro «a uno che
       *   si chiama così» rimetterebbe in piedi proprio quello — e nella
       *   direzione peggiore, dichiarando pagato uno che non ha pagato.
       */
      return p.memberId !== null && p.memberId === chi.memberId;
    })
    .reduce((s, p) => s + p.amountCents, 0);
}

/** Quello che resta da prendere a questa persona, mai sotto zero. */
export function residuoCents(
  cassa: CassaDellaPrenotazione,
  chi: ChiPaga,
  inCampo: readonly ChiPaga[],
): number {
  return Math.max(chi.amountDueCents - giaPagatoCents(cassa, chi, inCampo), 0);
}

export const inEuro = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');

/**
 * ⭐ Da dove parte la riga di ciascuno: **da nessun numero battuto a mano**.
 *
 * 💶 L'importo mostrato è il **residuo**, non quello a carico: se metà è già
 * stata pagata, riproporre il totale è il modo più diretto per far incassare due
 * volte. Resta scritto anche a riga non spuntata — spento, ma leggibile: è la
 * risposta a «quanto mi deve?», che è la domanda che si fa PRIMA di spuntare.
 *
 * 🔄🚨⭐⭐ **E DAL 18/09/2026 NON È PIÙ UNA FOTOGRAFIA: è UN LEGAME.**
 * 🗣️ Sue parole, con davanti una riga da **20,00 €** a carico che voleva
 *   incassarne **10,00**: *«se io cambio a carico deve anche cambiare
 *   incassa»*.
 * ⚖️ Qui dentro l'importo nasceva **copiato** dal residuo nell'istante in cui
 *   la riga veniva toccata la prima volta — e da quel momento non lo guardava
 *   più nessuno. Cambiare «a carico» aggiornava la colonna accanto e lasciava
 *   la casella dell'incasso ferma sulla cifra vecchia: **la scheda diceva due
 *   numeri diversi della stessa cosa**, e quello che partiva alla cassa era il
 *   più vecchio dei due.
 * ⇒ `null` non vuol dire «vuoto»: vuol dire **«segui il residuo»**. Finché
 *   nessuno ha battuto una cifra a mano, la casella è un **calcolo**, non un
 *   valore — e un calcolo si rifà da sé appena cambia ciò da cui dipende.
 * 📌 *Un numero copiato da un altro è giusto una volta sola: al secondo in
 *    cui l'originale cambia, la copia non è vecchia — mente.*
 */
export type SceltaGrezza = {
  scelto: boolean;
  /** `null` = nessuno l'ha battuto: la casella mostra il residuo di adesso. */
  importo: string | null;
  metodo: MetodoIncasso;
};

/**
 * ⭐ Com'è una riga che nessuno ha ancora toccato.
 *
 * 🔄 **La spunta parte VUOTA, e per un'ora del 17/09/2026 partiva messa.**
 * Aveva senso quando l'incasso si apriva con un bottone: quel bottone era la
 * dichiarazione d'intenzione, e le spunte messe erano il seguito. Adesso la
 * tabella è sempre quella e «Incassa» sta sempre nel piede ⇒ righe già spuntate
 * vorrebbero dire che **un clic solo, dato per sbaglio, incassa da tutti**.
 * 📌 *Un valore predefinito è innocuo finché serve un gesto per arrivarci: se
 *    il gesto sparisce, il valore predefinito diventa una scelta fatta da
 *    qualcun altro.*
 */
export const RIGA_INTATTA: SceltaGrezza = { scelto: false, importo: null, metodo: 'cash' };

/**
 * ⭐⭐ **CHE CIFRA MOSTRA LA CASELLA «INCASSA» DI QUESTA RIGA.**
 *
 * ⇒ È qui che vive la risposta a *«se io cambio a carico deve anche cambiare
 * incassa»*: con `battuto` a `null` la casella **non è un valore, è un
 * calcolo** — rifatto a ogni disegno, sul `chi` che la scheda ricostruisce a
 * ogni rilettura dal server. Cambiato l'a carico, cambia da sé.
 * ⚠️ Con una cifra battuta a mano vince quella, e resta finché non la si
 * rimette a seguire (⇒ `seguiIlCarico`): una cifra scritta da chi sta al banco
 * non la sovrascrive nessun calcolo.
 */
export function importoDellaRiga(
  battuto: string | null,
  cassa: CassaDellaPrenotazione,
  chi: ChiPaga,
  inCampo: readonly ChiPaga[],
): string {
  return battuto ?? inEuro(residuoCents(cassa, chi, inCampo));
}
